import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, ChevronDown, Check, X } from 'lucide-react';
import { apiFetch } from '../api/client';

interface DbEntry { id: number; name: string; }
interface Company { company_name: string; company_api_id: number; db_id: number; }
interface Area { area_name: string; area_api_id: number; area_id: number; }

interface AxisRow { axis_num: number; name: string; enabled: boolean; }
interface PointRow { index: number; name: string; axes: AxisRow[]; rpm: string; }

const DEFAULT_AXES: AxisRow[] = [
  { axis_num: 1, name: 'H', enabled: true },
  { axis_num: 2, name: 'V', enabled: true },
  { axis_num: 3, name: 'A', enabled: true },
];

function newPoint(index: number): PointRow {
  return { index, name: '', axes: DEFAULT_AXES.map(a => ({ ...a })), rpm: '' };
}

// ---------------------------------------------------------------------------
// Inline-create dropdown for a single tier (Database / Company / Area)
// ---------------------------------------------------------------------------
interface TierSelectProps {
  label: string;
  value: string;
  placeholder: string;
  options: { label: string; key: string }[];
  disabled?: boolean;
  onSelect: (key: string) => void;
  onCreateNew: (name: string) => Promise<void>;
  creating?: boolean;
}

function TierSelect({
  label, value, placeholder, options, disabled,
  onSelect, onCreateNew, creating,
}: TierSelectProps) {
  const [open, setOpen] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setAddMode(false);
        setNewName('');
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await onCreateNew(newName.trim());
      setAddMode(false);
      setNewName('');
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <label className="block text-sm text-gray-600 mb-1">{label}</label>
      <div className="flex items-center gap-2" ref={ref}>
        <div className="relative flex-1">
          <button
            type="button"
            disabled={disabled}
            onClick={() => { setOpen(!open); setAddMode(false); }}
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-left hover:border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className={value ? 'text-gray-800' : 'text-gray-400'}>
              {value || placeholder}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </button>

          {open && (
            <div className="absolute z-30 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-52 overflow-y-auto">
              {options.length === 0 && (
                <p className="px-3 py-2 text-sm text-gray-400 italic">No options yet</p>
              )}
              {options.map(opt => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => { onSelect(opt.key); setOpen(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50"
                >
                  <span className="text-gray-800">{opt.label}</span>
                  {value === opt.label && <Check className="w-4 h-4 text-[#00D4BE]" />}
                </button>
              ))}

              {addMode ? (
                <div className="px-2 py-2 border-t border-gray-100 flex items-center gap-1.5">
                  <input
                    autoFocus
                    type="text"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setAddMode(false); setNewName(''); } }}
                    placeholder={`New ${label.toLowerCase()} name`}
                    className="flex-1 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#00D4BE]/60"
                  />
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={saving || !newName.trim()}
                    className="px-2 py-1 bg-[#00D4BE] text-white text-xs rounded hover:bg-[#00bfab] disabled:opacity-50"
                  >
                    {saving ? '…' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddMode(false); setNewName(''); }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddMode(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#00D4BE] border-t border-gray-100 hover:bg-gray-50"
                >
                  <Plus className="w-3.5 h-3.5" />
                  New {label.toLowerCase()}…
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function AddMachine({ onSaved }: { onSaved?: () => void }) {
  const [databases, setDatabases] = useState<DbEntry[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);

  const [selectedDb, setSelectedDb] = useState<DbEntry | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);

  const [machineName, setMachineName] = useState('');
  const [operatingRpm, setOperatingRpm] = useState('');
  const [points, setPoints] = useState<PointRow[]>([newPoint(1)]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load databases on mount
  useEffect(() => {
    apiFetch('/setup/databases')
      .then((data: DbEntry[]) => setDatabases(data))
      .catch(() => setError('Failed to load databases.'));
  }, []);

  // Load companies when db changes
  useEffect(() => {
    if (!selectedDb) { setCompanies([]); setSelectedCompany(null); return; }
    apiFetch(`/setup/companies?db_id=${selectedDb.id}`)
      .then((data: Company[]) => { setCompanies(data); setSelectedCompany(null); setAreas([]); setSelectedArea(null); })
      .catch(() => setError('Failed to load companies.'));
  }, [selectedDb]);

  // Load areas when company changes
  useEffect(() => {
    if (!selectedDb || !selectedCompany) { setAreas([]); setSelectedArea(null); return; }
    apiFetch(`/setup/areas?db_id=${selectedDb.id}&company_name=${encodeURIComponent(selectedCompany.company_name)}`)
      .then((data: Area[]) => { setAreas(data); setSelectedArea(null); })
      .catch(() => setError('Failed to load areas.'));
  }, [selectedCompany, selectedDb]);

  // Handlers for creating new entries
  const handleNewDatabase = async (name: string) => {
    const result: DbEntry = await apiFetch('/setup/databases', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    setDatabases(prev => [...prev, result].sort((a, b) => a.name.localeCompare(b.name)));
    setSelectedDb(result);
  };

  const handleNewCompany = async (name: string) => {
    if (!selectedDb) return;
    // Company doesn't need a backend call — it's denormalized on machines.
    // Represent it locally with api_id=0 (no EI-Analytic mapping).
    const fake: Company = { company_name: name, company_api_id: 0, db_id: selectedDb.id };
    setCompanies(prev => [...prev, fake].sort((a, b) => a.company_name.localeCompare(b.company_name)));
    setSelectedCompany(fake);
  };

  const handleNewArea = async (name: string) => {
    if (!selectedDb) return;
    // Area: pick a safe area_id above current max to avoid collision, or use 0
    const maxAreaId = areas.reduce((m, a) => Math.max(m, a.area_id), 0);
    const fake: Area = { area_name: name, area_api_id: 0, area_id: maxAreaId + 1 };
    setAreas(prev => [...prev, fake].sort((a, b) => a.area_name.localeCompare(b.area_name)));
    setSelectedArea(fake);
  };

  // Points helpers
  const addPoint = () => setPoints(prev => [...prev, newPoint(prev.length + 1)]);
  const removePoint = (idx: number) =>
    setPoints(prev => prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, index: i + 1 })));
  const updatePoint = (idx: number, field: 'name' | 'rpm', value: string) =>
    setPoints(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  const toggleAxis = (pointIdx: number, axisNum: number) =>
    setPoints(prev => prev.map((p, i) =>
      i !== pointIdx ? p : {
        ...p,
        axes: p.axes.map(a => a.axis_num === axisNum ? { ...a, enabled: !a.enabled } : a),
      }
    ));

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (!selectedDb) { setError('Select a database.'); return; }
    if (!selectedCompany) { setError('Select a company.'); return; }
    if (!selectedArea) { setError('Select an area.'); return; }
    if (!machineName.trim()) { setError('Enter a machine name.'); return; }
    if (points.some(p => !p.name.trim())) { setError('All points must have a name.'); return; }
    if (points.some(p => p.axes.every(a => !a.enabled))) { setError('Each point must have at least one axis enabled.'); return; }

    setSaving(true);
    try {
      const result = await apiFetch('/setup/machines', {
        method: 'POST',
        body: JSON.stringify({
          db_id: selectedDb.id,
          company_name: selectedCompany.company_name,
          company_api_id: selectedCompany.company_api_id,
          area_name: selectedArea.area_name,
          area_api_id: selectedArea.area_api_id,
          area_id: selectedArea.area_id,
          name: machineName.trim(),
          operating_rpm: operatingRpm ? parseFloat(operatingRpm) : null,
          points: points.map(p => ({
            index: p.index,
            name: p.name.trim(),
            axes: p.axes.filter(a => a.enabled).map(a => ({ axis_num: a.axis_num, name: a.name })),
            rpm: p.rpm ? parseFloat(p.rpm) : null,
          })),
        }),
      });
      setSuccess(`"${result.name}" saved — ${result.points_created} point(s), ${result.axes_created} axis(es).`);
      setMachineName('');
      setOperatingRpm('');
      setPoints([newPoint(1)]);
      onSaved?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save machine.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-gray-800 mb-6">Add Machine</h1>

      <div className="flex gap-6">
        {/* Left panel */}
        <div className="w-72 flex-shrink-0 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Machine Info</h2>

            <TierSelect
              label="Database"
              value={selectedDb?.name ?? ''}
              placeholder="Select database…"
              options={databases.map(d => ({ label: d.name, key: String(d.id) }))}
              onSelect={key => {
                const db = databases.find(d => d.id === Number(key)) ?? null;
                setSelectedDb(db);
              }}
              onCreateNew={handleNewDatabase}
            />

            <TierSelect
              label="Company"
              value={selectedCompany?.company_name ?? ''}
              placeholder="Select company…"
              options={companies.map(c => ({ label: c.company_name, key: c.company_name }))}
              disabled={!selectedDb}
              onSelect={key => setSelectedCompany(companies.find(c => c.company_name === key) ?? null)}
              onCreateNew={handleNewCompany}
            />

            <TierSelect
              label="Area"
              value={selectedArea?.area_name ?? ''}
              placeholder="Select area…"
              options={areas.map(a => ({ label: a.area_name, key: String(a.area_id) }))}
              disabled={!selectedCompany}
              onSelect={key => setSelectedArea(areas.find(a => a.area_id === Number(key)) ?? null)}
              onCreateNew={handleNewArea}
            />

            <div>
              <label className="block text-sm text-gray-600 mb-1">Machine Name</label>
              <input
                type="text"
                value={machineName}
                onChange={e => setMachineName(e.target.value)}
                placeholder="e.g. Fan Drive Motor"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00D4BE]/60 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Operating RPM <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="number"
                value={operatingRpm}
                onChange={e => setOperatingRpm(e.target.value)}
                placeholder="e.g. 1480"
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#00D4BE]/60 transition-colors"
              />
            </div>
          </div>

          <div className="space-y-2">
            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
            )}
            {success && (
              <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{success}</p>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="w-full py-2.5 bg-[#00D4BE] text-white font-semibold rounded-lg hover:bg-[#00bfab] transition-colors disabled:opacity-60 text-sm"
            >
              {saving ? 'Saving…' : 'Save Machine'}
            </button>
          </div>
        </div>

        {/* Right panel — points */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Measurement Points</h2>
            <button
              type="button"
              onClick={addPoint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#00D4BE]/10 text-[#00D4BE] rounded-lg text-sm font-medium hover:bg-[#00D4BE]/20 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Point
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-100">
                  <th className="px-4 py-3 text-left w-12">#</th>
                  <th className="px-4 py-3 text-left">Point Name</th>
                  <th className="px-4 py-3 text-left">Axes</th>
                  <th className="px-4 py-3 text-left w-28">RPM Override</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody>
                {points.map((pt, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-gray-400 font-mono">{pt.index}</td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={pt.name}
                        onChange={e => updatePoint(i, 'name', e.target.value)}
                        placeholder="e.g. Motor DE"
                        className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:border-[#00D4BE]/60 transition-colors text-sm"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {pt.axes.map(ax => (
                          <button
                            key={ax.axis_num}
                            type="button"
                            onClick={() => toggleAxis(i, ax.axis_num)}
                            className={`w-8 h-7 rounded text-xs font-semibold transition-colors ${
                              ax.enabled
                                ? 'bg-[#00D4BE] text-white'
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }`}
                          >
                            {ax.name}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={pt.rpm}
                        onChange={e => updatePoint(i, 'rpm', e.target.value)}
                        placeholder="—"
                        className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:border-[#00D4BE]/60 transition-colors text-sm"
                      />
                    </td>
                    <td className="px-4 py-3">
                      {points.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removePoint(i)}
                          className="p-1 text-gray-300 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
